import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import _ from "lodash";

import { axiosInstance } from "../utils/axios";
import { useStandingsContext } from "./standings";

// Season links point at /seasons/{id}/schedule, so ids are read back out of the href
const parseSeasonIdFromHref = (href) => {
  const match = href ? href.match(/\/seasons\/(\d+)/) : null;
  return match ? Number(match[1]) : null;
};

// Create a context with default values
const ScheduleContext = createContext({
  seasons: {},
  currentSeasonId: null,
  standingsSeasonId: null,
});

// Custom hook to use the SeasonScheduleContext
export const useScheduleContext = () => {
  return useContext(ScheduleContext);
};

// SeasonScheduleProvider component that wraps your app
export const ScheduleProvider = ({ children }) => {
  const { loadSeason: loadSeasonStandings } = useStandingsContext();
  const [seasons, setSeasons] = useState({});
  const [currentSeasonId, setCurrentSeasonId] = useState(null);
  // The season to show standings for, which is not the current one while a new season has yet to
  // race: the backend resolves it from scored results and names it in the standings-season rel.
  const [standingsSeasonId, setStandingsSeasonId] = useState(null);
  // Track which season IDs have been fetched so we don't loop on empty-race seasons
  const fetchedSeasonIds = useRef(new Set());
  const seasonCache = useRef({});
  const currentSeasonLoaded = useRef(false);

  const fetchSeasonSchedule = useCallback(async (season) => {
    const { data } = await axiosInstance.get(`/seasons/${season}/schedule`);
    return data;
  }, []);

  const mergeSeasonObjects = useCallback((existing, incoming) => {
    if (!existing && !incoming) return null;
    const base = existing || {};
    const src = incoming || {};
    return {
      ...base,
      ...src,
      _links: {
        ...(base._links || {}),
        ...(src._links || {}),
      },
      races: (base.races && base.races.length > 0)
        ? base.races
        : (src.races || []),
    };
  }, []);

  const loadSeasonSchedule = useCallback(
    async (seasonId) => {
      if (fetchedSeasonIds.current.has(seasonId)) {
        return seasonCache.current[seasonId] ?? null;
      }

      fetchedSeasonIds.current.add(seasonId);
      const data = await fetchSeasonSchedule(seasonId);
      if (data) {
        seasonCache.current[data.id] = mergeSeasonObjects(seasonCache.current[data.id], data);
      }
      return data;
    },
    [fetchSeasonSchedule, mergeSeasonObjects],
  );

  const mergeSeasonsList = useCallback((list) => {
    const processSeason = (season, cache, updated) => {
      if (!season) return;
      cache[season.id] = mergeSeasonObjects(cache[season.id], season);
      if (updated) {
        updated[season.id] = mergeSeasonObjects(updated[season.id], season);
      }
      if (season.childSeasons && season.childSeasons.length > 0) {
        season.childSeasons.forEach((child) => {
          processSeason(child, cache, updated);
        });
      }
    };

    list.forEach((season) => {
      processSeason(season, seasonCache.current, null);
    });

    setSeasons((prevValue) => {
      const updated = { ...prevValue };
      list.forEach((season) => {
        processSeason(season, seasonCache.current, updated);
      });
      return updated;
    });
  }, [mergeSeasonObjects]);


  const loadSeasonsByType = useCallback(
    async (seasonType) => {
      const { data } = await axiosInstance.get("/seasons", {
        params: { seasonType, size: 100 },
      });
      const list = data._embedded?.seasons || [];
      mergeSeasonsList(list);
      return list;
    },
    [mergeSeasonsList],
  );

  const loadSeasonsBySimulator = useCallback(
    async (simulator) => {
      const { data } = await axiosInstance.get("/seasons", {
        params: { simulator, seasonType: "LEAGUE_SEASON", size: 100 },
      });
      const list = data._embedded?.seasons || [];
      mergeSeasonsList(list);
      return list;
    },
    [mergeSeasonsList],
  );

  const loadSeasonMeta = useCallback(
    async (seasonId) => {
      const { data } = await axiosInstance.get(`/seasons/${seasonId}`);
      mergeSeasonsList([data]);
      return data;
    },
    [mergeSeasonsList],
  );

  const loadSchedule = useCallback(
    async (seasonIds, preloadPrev = false) => {
      if (!_.isArray(seasonIds)) {
        seasonIds = [seasonIds];
      }

      const results = await Promise.all(seasonIds.map(loadSeasonSchedule));

      const resultObj = {};
      _.forEach(results, (season) => {
        if (season) resultObj[season.id] = season;
      });

      setSeasons((prevValue) => {
        const updated = { ...prevValue };
        _.forEach(resultObj, (season, id) => {
          updated[id] = mergeSeasonObjects(prevValue[id], season);
        });
        return updated;
      });

      // Preload only the immediate prev of the selected season (one step ahead)
      const selectedId = seasonIds[0];
      const selectedSeasonObj = resultObj[selectedId] ?? seasonCache.current[selectedId];

      if (preloadPrev) {
        loadSeasonStandings(selectedId);

        const season = selectedSeasonObj;
        const prevId = parseSeasonIdFromHref(season?._links?.prev?.href);
        if (prevId) {
          loadSeasonStandings(prevId);

          if (!fetchedSeasonIds.current.has(prevId)) {
            const prevSeason = await loadSeasonSchedule(prevId);
            if (prevSeason) {
              setSeasons((prevValue) => ({
                ...prevValue,
                [prevSeason.id]: mergeSeasonObjects(prevValue[prevSeason.id], prevSeason),
              }));
            }
          }
        }
      }
    },
    [loadSeasonSchedule, mergeSeasonObjects, loadSeasonStandings],
  );

  // Load current season and all seasons metadata on mount
  useEffect(() => {
    if (currentSeasonLoaded.current) return;
    currentSeasonLoaded.current = true;

    (async () => {
      try {
        const { data } = await axiosInstance.get("/seasons/current");
        const seasonId = data.id;
        setCurrentSeasonId(seasonId);
        setStandingsSeasonId(parseSeasonIdFromHref(data._links?.["standings-season"]?.href));
        mergeSeasonsList([data]);

        // Preload current season's schedule
        loadSchedule(seasonId);

        // Fetch all seasons metadata to statically populate seasonsList and prevent Swiper re-render glitches
        const { data: allSeasonsData } = await axiosInstance.get("/seasons", {
          params: { size: 100 },
        });
        const list = allSeasonsData._embedded?.seasons || [];
        mergeSeasonsList(list);
      } catch (e) {
        console.error("Failed to load current season", e);
      }
    })();
  }, [mergeSeasonsList, loadSchedule]);


  return (
    <ScheduleContext.Provider value={{
      seasons,
      currentSeasonId,
      standingsSeasonId,
      loadSchedule,
      loadSeasonsByType,
      loadSeasonsBySimulator,
      loadSeasonMeta,
    }}>
      {children}
    </ScheduleContext.Provider>
  );
};
